import React from "react";
import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import MenuGallery from "../components/MenuGallery";
import StarRating from "../components/StarRating";

const detailStyles = `
/* Hero image */
.tr-kdetail__hero {
  max-width: 1180px;
  margin: 24px auto 32px;
  padding: 0 24px;
  box-sizing: border-box;
  width: 100%;
}
.tr-kdetail__hero-img {
  width: 100%;
  aspect-ratio: 16/7;
  object-fit: cover;
  border-radius: 32px;
  display: block;
  background: #e5e7eb;
}
.tr-kdetail__hero-placeholder {
  width: 100%;
  aspect-ratio: 16/7;
  border-radius: 32px;
  background: linear-gradient(135deg, #e5e7eb, #cbd5e1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

/* Container */
.tr-kdetail__body {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
  width: 100%;
}

/* Title */
.tr-kdetail__title {
  font-size: clamp(26px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 20px;
  line-height: 1.2;
}

/* Grid 2 colonnes */
.tr-kdetail__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 380px);
  gap: 40px;
  align-items: start;
}
.tr-kdetail__grid > * { min-width: 0; }
@media (max-width: 900px) {
  .tr-kdetail__grid {
    grid-template-columns: 1fr;
  }
}

/* ── Colonne gauche ── */
.tr-kdetail__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}
.tr-kdetail__tag {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 999px;
  background: #ffe8d9;
  color: #1a1a2e;
}
.tr-kdetail__tag--meat   { background: #fecaca; color: #991b1b; }
.tr-kdetail__tag--dairy  { background: #dbeafe; color: #1e40af; }
.tr-kdetail__tag--parve  { background: #dcfce7; color: #166534; }

.tr-kdetail__kashrut {
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.5;
}
.tr-kdetail__kashrut b {
  color: #1a1a2e;
  font-weight: 700;
  margin-inline-end: 4px;
}

/* Boutons ronds action */
.tr-kdetail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 24px;
}
.tr-kdetail__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 500;
}
.tr-kdetail__action-btn {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary, #1b579e);
  transition: transform .15s, box-shadow .15s;
}
.tr-kdetail__action:hover .tr-kdetail__action-btn {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px color-mix(in srgb, var(--primary, #1b579e) 25%, transparent);
}

/* Horaires */
.tr-kdetail__hours-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 40px 0 14px;
}
.tr-kdetail__hours {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  row-gap: 10px;
  column-gap: 12px;
  font-size: 14px;
  color: #374151;
  max-width: 100%;
  width: 420px;
}
@media (max-width: 480px) { .tr-kdetail__hours { width: 100%; } }
.tr-kdetail__hours-day {
  color: #6b7280;
  text-transform: lowercase;
}
.tr-kdetail__hours-slots {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #1a1a2e;
}
.tr-kdetail__hours-closed {
  color: #9ca3af;
  font-style: italic;
}

/* Rich comment */
.tr-kdetail__comment {
  margin-top: 18px;
  font-size: 15px;
  line-height: 1.7;
  color: #1f2937;
  max-width: 720px;
  overflow-wrap: anywhere;
}
.tr-kdetail__comment img { max-width: 100%; height: auto; }
.tr-kdetail__comment h1,
.tr-kdetail__comment h2,
.tr-kdetail__comment h3 { color: #1a1a2e; margin-top: 24px; margin-bottom: 10px; }
.tr-kdetail__comment p { margin: 0 0 12px; }
.tr-kdetail__comment a { color: var(--primary, #1b579e); text-decoration: underline; }

/* ── Colonne droite : carte infos ── */
.tr-kdetail__side {
  background: white;
  border-radius: 24px;
  padding: 22px 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,.06), 0 1px 4px rgba(0,0,0,.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 88px;
  max-width: 100%;
  box-sizing: border-box;
}
.tr-kdetail__side-row {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #374151;
  font-size: 14px;
  line-height: 1.4;
  min-width: 0;
}
.tr-kdetail__side-row span:last-child { min-width: 0; overflow-wrap: anywhere; }
.tr-kdetail__side-row:hover { color: #1a1a2e; }
.tr-kdetail__side-icon {
  width: 40px; height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tr-kdetail__side-icon--location,
.tr-kdetail__side-icon--phone,
.tr-kdetail__side-icon--email,
.tr-kdetail__side-icon--whatsapp {
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  color: var(--primary, #1b579e);
}

/* Back link */
.tr-kdetail__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 18px;
  text-decoration: none;
}
.tr-kdetail__back:hover { text-decoration: underline; }
`;

const DAYS_ORDER = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday"];



function ActionButton({ type, href, label, icon, external }) {
    return (
        <a
            href={href}
            className={`tr-kdetail__action tr-kdetail__action--${type}`}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
        >
            <span className="tr-kdetail__action-btn">
                {renderIcon(icon, 26)}
            </span>
            <span>{label}</span>
        </a>
    );
}

export default function KosherPlaceDetail({ context, loading }) {
    const { org, config, travel } = context;

    const placeId = context.route?.[2];
    const rawPlace = (travel?.kosherPlaces || []).find(p => p.id === placeId);

    const DAY_LABELS = {
        sunday: t`dimanche`,
        monday: t`lundi`,
        tuesday: t`mardi`,
        wednesday: t`mercredi`,
        thursday: t`jeudi`,
        friday: t`vendredi`,
    };

    const MEAT_TYPE_LABELS = {
        meat: t`Meat`,
        dairy: t`Lacté`,
        parve: t`Parvé`,
    };

    if (!rawPlace) {
        return (
            <div className="travel-page travel-page--kosher-detail">
                <Meta context={context} title={org.name} description={org.name} />
                {loading && <Loader />}
                <Header context={context} />
                <main className="travel-main">
                    <div className="travel-container" style={{ padding: "80px 24px", textAlign: "center" }}>
                        <h1><Trans>Lieu introuvable</Trans></h1>
                        <Link href={org.BASE_WEB_URL + "/travel/kosher-places"} legacyBehavior>
                            <a className="tr-kdetail__back">
                                {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                                <Trans>Retour à la liste</Trans>
                            </a>
                        </Link>
                    </div>
                </main>
                <Footer context={context} />
            </div>
        );
    }

    const place = applyLang(rawPlace, context.lang);
    const categories = (travel?.kosher?.categories || []).map(c => applyLang(c, context.lang));
    const category = categories.find(c => c.id === place.categoryId) || null;

    const imgSrc = place.image ? `${ASSET_URL}${place.image}` : null;
    const address = place.address?.address;
    const lat = place.address?.lat;
    const lon = place.address?.lon;
    const navUrl = lat != null && lon != null
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
        : (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null);

    const contacts = place.contacts || [];
    const phoneContact = contacts.find(c => c.type === "phone");
    const emailContact = contacts.find(c => c.type === "email");
    const whatsappContact = contacts.find(c => c.type === "whatsapp");

    const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();
    const placeDesc = stripHtml(place.comment) || t`${place.name} — lieu cachère à ${org.ville || org.name}`;

    // Hours -> openingHoursSpecification (Schema.org)
    const DAY_MAP = { sunday: "Sunday", monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday" };
    const openingHours = [];
    if (place.hours) {
        for (const [day, slots] of Object.entries(place.hours)) {
            if (!Array.isArray(slots)) continue;
            for (const s of slots) {
                if (!s.open || !s.close) continue;
                openingHours.push({
                    "@type": "OpeningHoursSpecification",
                    dayOfWeek: DAY_MAP[day] || day,
                    opens: s.open,
                    closes: s.close,
                });
            }
        }
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Restaurant",
        name: place.name,
        description: placeDesc,
        image: imgSrc || undefined,
        servesCuisine: "Kosher",
        address: address ? { "@type": "PostalAddress", streetAddress: address } : undefined,
        geo: (lat != null && lon != null) ? {
            "@type": "GeoCoordinates",
            latitude: lat,
            longitude: lon,
        } : undefined,
        telephone: phoneContact?.value,
        email: emailContact?.value,
        openingHoursSpecification: openingHours.length > 0 ? openingHours : undefined,
    };

    return (
        <div className="travel-page travel-page--kosher-detail tr-kdetail">
            <style dangerouslySetInnerHTML={{ __html: detailStyles }} />
            <Meta
                context={context}
                title={place.name}
                description={placeDesc.slice(0, 200)}
                image={imgSrc}
                type="restaurant.restaurant"
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">

                {/* Hero image */}
                <div className="tr-kdetail__hero">
                    {imgSrc ? (
                        <img src={imgSrc} alt={place.name} className="tr-kdetail__hero-img" />
                    ) : (
                        <div className="tr-kdetail__hero-placeholder">
                            {renderIcon({ name: "IconBuildingStore", source: "tabler" }, 64)}
                        </div>
                    )}
                </div>

                <div className="tr-kdetail__body">

                    <Link href={org.BASE_WEB_URL + "/travel/kosher-places"} legacyBehavior>
                        <a className="tr-kdetail__back">
                            {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                            <Trans>Retour</Trans>
                        </a>
                    </Link>

                    <h1 className="tr-kdetail__title">{place.name}</h1>

                    {place.note > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <StarRating value={place.note} size={20} showValue />
                        </div>
                    )}

                    <div className="tr-kdetail__grid">

                        {/* Colonne gauche */}
                        <div>
                            <div className="tr-kdetail__tags">
                                {category?.label && (
                                    <span className="tr-kdetail__tag">{category.label}</span>
                                )}
                                {(place.meatType || []).map(mt => (
                                    <span key={mt} className={`tr-kdetail__tag tr-kdetail__tag--${mt}`}>
                                        {MEAT_TYPE_LABELS[mt] || mt}
                                    </span>
                                ))}
                            </div>

                            {place.kashrut && (
                                <div className="tr-kdetail__kashrut">
                                    <b><Trans>Kashrut :</Trans></b>{place.kashrut}
                                </div>
                            )}
                            {place.kashrutComment && (
                                <div className="tr-kdetail__kashrut">
                                    <b><Trans>Commentaire Kashrut :</Trans></b>{place.kashrutComment}
                                </div>
                            )}

                            {/* Commentaire HTML */}
                            {place.comment && (
                                <div
                                    className="tr-kdetail__comment"
                                    dangerouslySetInnerHTML={{ __html: place.comment }}
                                />
                            )}

                            {/* Actions */}
                            <div className="tr-kdetail__actions">
                                {phoneContact && (
                                    <ActionButton
                                        type="phone"
                                        href={`tel:${phoneContact.value}`}
                                        label={t`Appel`}
                                        icon={{ name: "IconPhone", source: "tabler" }}
                                    />
                                )}
                                {navUrl && (
                                    <ActionButton
                                        type="nav"
                                        href={navUrl}
                                        label={t`Naviguer`}
                                        icon={{ name: "IconMapPin", source: "tabler" }}
                                        external
                                    />
                                )}
                                {whatsappContact && (
                                    <ActionButton
                                        type="whatsapp"
                                        href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`}
                                        label="WhatsApp"
                                        icon={{ name: "IconBrandWhatsapp", source: "tabler" }}
                                        external
                                    />
                                )}
                                {emailContact && (
                                    <ActionButton
                                        type="email"
                                        href={`mailto:${emailContact.value}`}
                                        label={t`E-mail`}
                                        icon={{ name: "IconMail", source: "tabler" }}
                                    />
                                )}
                                {place.orderUrl && (
                                    <ActionButton
                                        type="order"
                                        href={place.orderUrl}
                                        label={t`Site Internet`}
                                        icon={{ name: "IconHandClick", source: "tabler" }}
                                        external
                                    />
                                )}
                                {place.menuPdf && (
                                    <ActionButton
                                        type="menu"
                                        href={`${ASSET_URL}${place.menuPdf}`}
                                        label={t`Voir le menu`}
                                        icon={{ name: "IconToolsKitchen2", source: "tabler" }}
                                        external
                                    />
                                )}
                            </div>

                            {/* Horaires */}
                            {place.hours && Object.values(place.hours).some(slots => slots?.length > 0) && (
                                <>
                                    <h3 className="tr-kdetail__hours-title">
                                        <Trans>Horaires d'ouverture</Trans>
                                    </h3>
                                    <div className="tr-kdetail__hours">
                                        {DAYS_ORDER.map(day => {
                                            const slots = place.hours[day] || [];
                                            return (
                                                <React.Fragment key={day}>
                                                    <div className="tr-kdetail__hours-day">{DAY_LABELS[day]}</div>
                                                    <div className="tr-kdetail__hours-slots">
                                                        {slots.length === 0 ? (
                                                            <span className="tr-kdetail__hours-closed">
                                                                <Trans>Fermé</Trans>
                                                            </span>
                                                        ) : (
                                                            slots.map((s, i) => (
                                                                <span key={i}>{s.open}-{s.close}</span>
                                                            ))
                                                        )}
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {/* Galerie du menu */}
                            {place.menuImages?.length > 0 && (
                                <>
                                    <h3 className="tr-kdetail__hours-title">
                                        <Trans>Menu</Trans>
                                    </h3>
                                    <MenuGallery images={place.menuImages} />
                                </>
                            )}

                        </div>

                        {/* Colonne droite : carte latérale */}
                        <aside className="tr-kdetail__side">
                            {address && (
                                <a
                                    href={navUrl || "#"}
                                    className="tr-kdetail__side-row"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="tr-kdetail__side-icon tr-kdetail__side-icon--location">
                                        {renderIcon({ name: "IconMapPin", source: "tabler" }, 20)}
                                    </span>
                                    <span>{address}</span>
                                </a>
                            )}
                            {phoneContact && (
                                <a href={`tel:${phoneContact.value}`} className="tr-kdetail__side-row">
                                    <span className="tr-kdetail__side-icon tr-kdetail__side-icon--phone">
                                        {renderIcon({ name: "IconPhone", source: "tabler" }, 20)}
                                    </span>
                                    <span>{phoneContact.value}</span>
                                </a>
                            )}
                            {whatsappContact && (
                                <a
                                    href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`}
                                    className="tr-kdetail__side-row"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="tr-kdetail__side-icon tr-kdetail__side-icon--whatsapp">
                                        {renderIcon({ name: "IconBrandWhatsapp", source: "tabler" }, 20)}
                                    </span>
                                    <span>{whatsappContact.value}</span>
                                </a>
                            )}
                            {emailContact && (
                                <a href={`mailto:${emailContact.value}`} className="tr-kdetail__side-row">
                                    <span className="tr-kdetail__side-icon tr-kdetail__side-icon--email">
                                        {renderIcon({ name: "IconMail", source: "tabler" }, 20)}
                                    </span>
                                    <span>{emailContact.value}</span>
                                </a>
                            )}
                        </aside>

                    </div>

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
